import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { 
  Home, LogOut, User, TrendingUp, Calculator, 
  BarChart2, ClipboardList, Settings, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import OnboardingFlow from '../components/OnboardingFlow';
import FeatureTour from '../components/FeatureTour';
import OverviewTab from '../components/tabs/OverviewTab';
import DetailsTab from '../components/tabs/DetailsTab';
import StrategiesTab from '../components/tabs/StrategiesTab';
import RatesTab from '../components/tabs/RatesTab';
import RenewalPlannerTab from '../components/tabs/GoalsTab';
import SettingsTab from '../components/tabs/SettingsTab';
import AdminIntegrations from '../components/AdminIntegrations';
import ScraperMonitor from '../components/ScraperMonitor';
import { trackTabChange, trackLogout, trackUpgradeComplete } from '../utils/analytics';

const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL;

// Valid tab IDs
const VALID_TABS = ['overview', 'details', 'strategies', 'rates', 'planner', 'settings', 'admin'];

const Dashboard = ({ user: initialUser, defaultTab = 'overview' }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get initial tab from URL params or prop, fallback to 'overview'
  const getInitialTab = () => {
    const urlTab = searchParams.get('tab');
    if (urlTab && VALID_TABS.includes(urlTab)) {
      return urlTab;
    }
    // Handle legacy 'subscription' tab - redirect to 'settings'
    if (urlTab === 'subscription') {
      return 'settings';
    }
    return defaultTab;
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [adminSubTab, setAdminSubTab] = useState('integrations');
  const [user, setUser] = useState(initialUser);
  const [mortgage, setMortgage] = useState({});
  const [offers, setOffers] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [marketRates, setMarketRates] = useState({ rates: [], boc_rate: 0, economic_indicators: {} });
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showTour, setShowTour] = useState(false);
  
  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [mortgageRes, offersRes, scenariosRes, ratesRes] = await Promise.all([
        api.get('/api/mortgage'),
        api.get('/api/offers'),
        api.get('/api/scenarios'),
        api.get('/api/market-rates')
      ]);
      
      const mortgageData = mortgageRes.data || {};
      setMortgage(mortgageData);
      setOffers(offersRes.data || []);
      setScenarios(scenariosRes.data || []);
      setMarketRates(ratesRes.data);
      
      // Check if user needs onboarding
      const onboardingCompleted = localStorage.getItem('onboarding_completed');
      const tourCompleted = localStorage.getItem('tour_completed');
      
      if (!onboardingCompleted && !mortgageData.current_balance) {
        setShowOnboarding(true);
      } else if (!tourCompleted && mortgageData.current_balance) {
        setShowTour(true);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);
  
  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    setShowTour(true);
    loadData(); // Reload data after onboarding
  };
  
  const handleTourComplete = () => {
    setShowTour(false);
  };
  
  const handleLogout = async () => {
    trackLogout();
    try {
      await api.post('/api/auth/logout', {});
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear localStorage and navigate to login
      localStorage.removeItem('session_token');
      localStorage.removeItem('user_data');
      navigate('/login');
    }
  };
  
  const saveMortgage = async (mortgageData) => {
    try {
      await api.post('/api/mortgage', mortgageData);
      setMortgage(mortgageData);
      toast.success('Mortgage details saved');
      return true;
    } catch (error) {
      console.error('Error saving mortgage:', error);
      toast.error('Failed to save mortgage details');
      return false;
    }
  };
  
  const addOffer = async (offer) => {
    try {
      const response = await api.post('/api/offers', offer);
      setOffers([...offers, response.data]);
      toast.success('Offer added');
      return true;
    } catch (error) {
      console.error('Error adding offer:', error);
      toast.error('Failed to add offer');
      return false;
    }
  };
  
  const deleteOffer = async (offerId) => {
    try {
      await api.delete(`/api/offers/${offerId}`);
      setOffers(offers.filter(o => o.offer_id !== offerId));
      toast.success('Offer deleted');
    } catch (error) {
      console.error('Error deleting offer:', error);
      toast.error('Failed to delete offer');
    }
  };
  
  const addScenario = async (scenario) => {
    try {
      const response = await api.post('/api/scenarios', scenario);
      setScenarios([...scenarios, response.data]);
      toast.success('Scenario added');
      return true;
    } catch (error) {
      console.error('Error adding scenario:', error);
      toast.error('Failed to add scenario');
      return false;
    }
  };
  
  const deleteScenario = async (scenarioId) => {
    try {
      await api.delete(`/api/scenarios/${scenarioId}`);
      setScenarios(scenarios.filter(s => s.scenario_id !== scenarioId));
      toast.success('Scenario deleted');
    } catch (error) {
      console.error('Error deleting scenario:', error);
      toast.error('Failed to delete scenario');
    }
  };
  
  const refreshRates = async () => {
    try {
      toast.info('Fetching live rates...');
      const response = await api.get('/api/rates/refresh');
      setMarketRates(response.data);
      toast.success(`Rates updated from ${response.data.sources?.join(', ') || 'live sources'}`);
    } catch (error) {
      console.error('Error refreshing rates:', error);
      toast.error('Failed to refresh rates');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }
  
  // Show onboarding for new users
  if (showOnboarding) {
    return <OnboardingFlow user={user} onComplete={handleOnboardingComplete} />;
  }
  
  return (
    <>
      {/* Feature Tour */}
      {showTour && <FeatureTour onComplete={handleTourComplete} />}
      
      <div data-testid="dashboard" className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Home className="h-8 w-8 text-primary" />
              <div>
                <h1 className="text-xl font-heading font-bold">
                  My Mortgage <span className="text-primary">Canada</span>
                </h1>
                <p className="text-xs text-muted-foreground">Welcome back, {user?.name?.split(' ')[0]}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-3 py-1 bg-primary/10 rounded-lg">
                <div className="w-2 h-2 rounded-full bg-primary"></div>
                <span className="text-sm font-medium">
                  {user?.subscription_tier === 'free' && 'Free Plan'}
                  {user?.subscription_tier === 'premium' && 'Premium'}
                  {user?.subscription_tier === 'pro' && 'Pro'}
                </span>
              </div>
              
              {user?.email === ADMIN_EMAIL && (
                <Link
                  to="/admin"
                  data-testid="admin-dashboard-link"
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-purple-500/10 text-purple-400 border border-purple-500/30 rounded-lg hover:bg-purple-500/20 transition-colors"
                >
                  <Shield className="h-4 w-4" />
                  <span className="hidden sm:inline">Admin</span>
                </Link>
              )}
              
              <button
                onClick={handleLogout}
                data-testid="logout-btn"
                className="flex items-center gap-2 px-4 py-2 text-sm bg-card border border-border rounded-lg hover:bg-muted transition-colors"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Tab Navigation */}
      <div className="border-b border-border bg-card/30 backdrop-blur-sm sticky top-[73px] z-40">
        <div className="max-w-7xl mx-auto px-6">
          <nav className="flex gap-1 overflow-x-auto">
            {[
              { id: 'overview', label: 'Overview', icon: BarChart2 },
              { id: 'details', label: 'Mortgage Details', icon: Home },
              { id: 'strategies', label: 'Strategies', icon: Calculator },
              { id: 'rates', label: 'Market Rates', icon: TrendingUp },
              { id: 'planner', label: 'Renewal Planner', icon: ClipboardList },
              { id: 'settings', label: 'Settings', icon: Settings },
              // Admin tab - only visible to admin users
              ...(user?.email === ADMIN_EMAIL ? [{ id: 'admin', label: 'Admin', icon: Shield }] : [])
            ].map(tab => (
              <button
                key={tab.id}
                data-testid={`tab-${tab.id}`}
                onClick={() => {
                  setActiveTab(tab.id);
                  trackTabChange(tab.id);
                }}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>
      
      {/* Tab Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'overview' && (
          <OverviewTab 
            user={user}
            mortgage={mortgage} 
            offers={offers} 
            marketRates={marketRates}
            refreshRates={refreshRates}
          />
        )}
        {activeTab === 'details' && (
          <DetailsTab 
            mortgage={mortgage} 
            saveMortgage={saveMortgage}
            offers={offers}
          />
        )}
        {activeTab === 'strategies' && (
          <StrategiesTab 
            mortgage={mortgage}
            scenarios={scenarios}
            addScenario={addScenario}
            deleteScenario={deleteScenario}
            offers={offers}
            user={user}
          />
        )}
        {activeTab === 'rates' && (
          <RatesTab 
            marketRates={marketRates}
            refreshRates={refreshRates}
            user={user}
          />
        )}
        {activeTab === 'planner' && (
          <RenewalPlannerTab 
            mortgage={mortgage}
            marketRates={marketRates}
          />
        )}
        {activeTab === 'settings' && (
          <SettingsTab 
            user={user}
            setUser={setUser}
          />
        )}
        {activeTab === 'admin' && (
          <div className="space-y-6">
            {/* Admin Dashboard Link */}
            <div className="bg-card border border-border rounded-xl p-4 flex items-center justify-between">
              <div>
                <h3 className="font-semibold">Full Admin Dashboard</h3>
                <p className="text-sm text-muted-foreground">Manage blog posts, view analytics, and user data</p>
              </div>
              <Link 
                to="/admin"
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                <Shield className="h-4 w-4" />
                Open Admin Dashboard
              </Link>
            </div>
            
            {/* Admin Sub-tabs */}
            <div className="flex gap-2 p-1 bg-card border border-border rounded-lg w-fit">
              <button
                onClick={() => setAdminSubTab('integrations')}
                data-testid="admin-subtab-integrations"
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  adminSubTab === 'integrations'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                Integrations
              </button>
              <button
                onClick={() => setAdminSubTab('scrapers')}
                data-testid="admin-subtab-scrapers"
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  adminSubTab === 'scrapers'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                Scraper Monitor
              </button>
            </div>
            
            {/* Admin Sub-tab Content */}
            {adminSubTab === 'integrations' && <AdminIntegrations user={user} />}
            {adminSubTab === 'scrapers' && <ScraperMonitor user={user} />}
          </div>
        )}
      </main>
    </div>
    </>
  );
};

export default Dashboard;
